import { Button } from '@repo/studio-design-system-ui-button'
import { XCloseIcon } from '@repo/studio-design-system-ui-icons/general/x-close'
import { ScrollShadow } from '@repo/studio-design-system-ui-scroll-shadow'
import { BottomSheet } from '@repo/studio-design-system-ui-sheets/bottom-sheet'
import type { Breakpoints } from '@repo/studio-hooks-dom/use-breakpoint'
import { isBreakpointBelow, useBreakpoint } from '@repo/studio-hooks-dom/use-breakpoint'
import * as DialogPrimitive from 'primitives/dialog'
import * as React from 'react'
import { tw } from 'utils/classnames'

// Inline rather than a Tailwind arbitrary value: Tailwind rewrites the underscores in a token name
// to spaces there, leaving a reference that resolves to nothing and a scrim that never paints.
const PANEL_SCRIM_STYLE = {
  color: 'var(--theme_semantic_surface_pop-over_primary-background)',
} satisfies React.CSSProperties

interface DialogContextValue {
  isSheet: boolean
  titleId: string
  close: () => void
}

const DialogContext = React.createContext<DialogContextValue | null>(null)

function useDialogContext() {
  const context = React.useContext(DialogContext)

  if (!context) {
    throw new Error('Dialog parts must be rendered inside a Dialog.Root')
  }

  return context
}

export interface DialogRootProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Viewports narrower than this breakpoint get the BottomSheet instead of the centred dialog. */
  sheetBelow?: Breakpoints
  children: React.ReactNode
}

/**
 * Dialog — a modal overlay that interrupts the workflow to focus attention on a single task.
 *
 * On small viewports it becomes a BottomSheet, which the design system defines as the mobile form
 * of a dialog. Both shells take the same parts, so a layout transfers unchanged.
 *
 * @figma https://www.figma.com/design/mKWuIdPpAnPyyQnGc1pbY5/Studio-DS---Components?node-id=2557-15260
 */
function Root({ open, onOpenChange, sheetBelow = 'md', children }: DialogRootProps) {
  const breakpoint = useBreakpoint()
  const titleId = React.useId()
  const close = React.useCallback(() => onOpenChange(false), [onOpenChange])
  const isSheet = isBreakpointBelow(breakpoint, sheetBelow)
  const value = React.useMemo(() => ({ isSheet, titleId, close }), [isSheet, titleId, close])

  return (
    <DialogContext.Provider value={value}>
      {isSheet ? (
        <BottomSheet.Root open={open} onOpenChange={onOpenChange}>
          {children}
        </BottomSheet.Root>
      ) : (
        <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
          {children}
        </DialogPrimitive.Root>
      )}
    </DialogContext.Provider>
  )
}

export interface DialogContentProps {
  /** Merged into the panel, so a dialog that needs another width overrides it here. */
  className?: string
  children: React.ReactNode
}

function Content({ className, children }: DialogContentProps) {
  const { isSheet, titleId } = useDialogContext()

  if (isSheet) {
    return (
      <BottomSheet.Content
        aria-labelledby={titleId}
        aria-describedby={undefined}
        // The sheet's own inset stacks on top of every part's padding, and its own scrolling would
        // carry the header and footer away instead of leaving them stuck to the edges.
        className="[&>.content-container]:overflow-hidden [&>.content-container]:px-0 [&>.content-container]:pb-0"
        handleClassName="hidden"
      >
        <div className={tw(panelClasses.shared, className)}>{children}</div>
      </BottomSheet.Content>
    )
  }

  // `Body` renders a `fixed` panel but no portal, so without one the dialog stays inside its
  // caller's stacking context and later page content paints over it. `forceMount` keeps presence
  // with the AnimatePresence inside `Body`, which is why `Body` force-mounts its own content too.
  return (
    <DialogPrimitive.Portal forceMount>
      <DialogPrimitive.Body aria-labelledby={titleId} aria-describedby={undefined} className="p-4">
        <div className={tw(panelClasses.shared, panelClasses.dialog, className)}>{children}</div>
      </DialogPrimitive.Body>
    </DialogPrimitive.Portal>
  )
}

function Header({ className, ...props }: React.ComponentProps<'header'>) {
  return (
    <header
      {...props}
      className={tw('flex w-full shrink-0 items-start pl-6 pr-3 pt-3', className)}
    />
  )
}

/** Names the dialog for assistive technology. Without a visible title it still needs one, hidden. */
function Title({ className, children, ...props }: React.ComponentProps<'h2'>) {
  const { titleId } = useDialogContext()

  return (
    <h2
      id={titleId}
      {...props}
      className={tw(
        'studio-typography-18 min-w-px flex-1 pr-5 pt-2 text-theme_semantic_surface_ui_foreground-color-strong',
        className,
      )}
    >
      {children}
    </h2>
  )
}

export interface DialogCloseProps {
  /** Names the button for assistive technology, so the caller owns translating it. */
  label: string
  className?: string
}

function Close({ label, className }: DialogCloseProps) {
  const { close } = useDialogContext()

  return (
    <Button
      iconOnly
      variant="ghost"
      size="sm"
      aria-label={label}
      onClick={close}
      className={tw('ml-auto shrink-0', className)}
    >
      <XCloseIcon />
    </Button>
  )
}

/** Scrolls when the content outgrows the panel, scrimming the edge it continues past. */
function Body({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <ScrollShadow
      className="flex min-h-0 flex-1 flex-col"
      shadowClassName="h-12"
      shadowStyle={PANEL_SCRIM_STYLE}
    >
      <div {...props} className={tw('scroller-xs min-h-0 flex-1 px-6 pt-2', className)} />
    </ScrollShadow>
  )
}

function Footer({ className, ...props }: React.ComponentProps<'footer'>) {
  return (
    <footer
      {...props}
      className={tw('flex w-full shrink-0 items-center justify-end gap-2 px-6 pt-4', className)}
    />
  )
}

export const Dialog = {
  Root,
  Content,
  Header,
  Title,
  Close,
  Body,
  Footer,
}

/** The panel owns the bottom inset, so content still clears the edge when the footer is omitted. */
const panelClasses = {
  shared: 'flex max-h-full min-h-0 w-full flex-col overflow-clip pb-6',
  dialog: [
    'w-[548px] max-w-full rounded-studio-geometry-semantic-radius-lg',
    'border border-theme_semantic_surface_border_color-subtle',
    'bg-theme_semantic_surface_pop-over_primary-background shadow-studio-shadow-l5',
  ].join(' '),
}
